import type { TActivity2Fe } from "@timeedit/activity-manager-shared-lib/internal/types/Activity/Activity2.type";
import type { TActivityCreateTemplateFe } from "@timeedit/activity-manager-shared-lib/public/types/ActivityCreateTemplate.type";
import { formatDuration, getActivityTimingText } from "./ActivityDateTime.utils";
import Language from "../../lib/Language";
import {
    isObjectFilterValue,
    type TActivityRow,
    type TExtIdKind,
    toExtIdLines,
    toObjectFilter,
    toStringArray,
    toTextLines,
} from "./ActivityInfo.utils";

type TActivity2Value = TActivity2Fe["values"][number];

const getLabelsFromCreateTemplate = (
    template: TActivityCreateTemplateFe | undefined
): Map<string, string> => {
    const labels = new Map<string, string>();
    if (!template) {
        return labels;
    }
    [
        template.primaryObject,
        template.activityType,
        ...template.objects,
        ...template.fields,
    ].forEach(({ extId, label }) => labels.set(extId, label));
    template.objectFilters.forEach(({ typeExtId, label }) => labels.set(typeExtId, label));
    // Information fields have no ext id: their values are stored under the label.
    template.informationFields.forEach(({ label }) => labels.set(label, label));
    return labels;
};

const isObjectValue = (value: TActivity2Value) =>
    value.submissionValueType === "OBJECT" || value.submissionValueType === "FILTER";

const valueToRows = (value: TActivity2Value): Pick<TActivityRow, "lines" | "filters"> => {
    if (isObjectFilterValue(value.value)) {
        return { lines: [], filters: [toObjectFilter(value.extId, value.value)] };
    }
    if (isObjectValue(value)) {
        return { lines: toExtIdLines(toStringArray(value.value), "object"), filters: [] };
    }
    // Fields and information fields: strings or lists of them.
    return { lines: toTextLines(value.value), filters: [] };
};

const labelForExtId = (labels: Map<string, string>, extId: string, kind: TExtIdKind) => {
    const label = labels.get(extId);
    return label ? { text: label } : { extId, kind };
};

const timingRow = (key: string, label: string, text?: string): TActivityRow => ({
    key: `timing:${key}`,
    label: { text: label },
    lines: toTextLines(text),
    filters: [],
});

const timingToRows = (
    activity: TActivity2Fe,
    template: TActivityCreateTemplateFe | undefined
): TActivityRow[] => {
    const { startDate, endDate, length } = activity.metadata ?? {};
    const getTimingText = (extId: string) => {
        const timing = activity.timing?.find((entry) => entry.extId === extId);
        return timing ? getActivityTimingText(timing) : undefined;
    };

    const duration = typeof length === "number" && length >= 0 ? formatDuration(length) : undefined;

    const dateRange =
        startDate || endDate
            ? {
                  startTime: startDate || undefined,
                  endTime: endDate || undefined,
                  timeZone: activity.timezoneInfo?.identifier,
              }
            : undefined;

    const dateRangeRow = dateRange
        ? [{ ...timingRow("dateRange", Language.get("nc_activity_date_range")), dateRange }]
        : [];

    const rowsWithLines: TActivityRow[] = [
        timingRow(
            "duration",
            template?.duration?.label || Language.get("nc_activity_duration"),
            duration
        ),
        timingRow(
            "timezone",
            template?.timezone?.label || Language.get("cal_res_side_user_time_zone"),
            activity.timezoneInfo?.extId
        ),
        timingRow(
            "time",
            template?.preferredTime?.label || Language.get("nc_activity_preferred_time"),
            getTimingText("time")
        ),
        timingRow(
            "weekday",
            template?.preferredWeekday?.label || Language.get("nc_activity_preferred_weekday"),
            getTimingText("weekday")
        ),
        timingRow(
            "padding",
            template?.padding?.label || Language.get("nc_padding_title"),
            getTimingText("padding")
        ),
    ].filter((row) => row.lines.length > 0);

    return [...dateRangeRow, ...rowsWithLines];
};

export const activity2ToRows = (
    activity: TActivity2Fe,
    template: TActivityCreateTemplateFe | undefined
): TActivityRow[] => {
    const labels = getLabelsFromCreateTemplate(template);
    const slots = activity.objectSlots ?? [];
    const slotTypes = new Set(slots.map((slot) => slot.typeExtId));

    const slotRows = slots.map((slot) => ({
        key: `slot:${slot.typeExtId}`,
        label: labelForExtId(labels, slot.typeExtId, "type"),
        lines: slot.value.flatMap((slotValue) =>
            toExtIdLines(slotValue.assignedObjectExtIds, "object")
        ),
        filters: slot.value
            .filter((slotValue) => slotValue.objectFilters.length > 0)
            .map((slotValue) => ({
                typeExtId: slot.typeExtId,
                categories: slotValue.objectFilters,
            })),
    }));

    const valueRows = activity.values
        .filter((value) => !slotTypes.has(value.extId))
        .map((value) => ({
            key: `value:${value.extId}`,
            label: labelForExtId(
                labels,
                value.extId,
                value.submissionValueType === "FREE_TEXT" ? "field" : "type"
            ),
            ...valueToRows(value),
        }));

    const computedRows = (activity.computedValues ?? []).map((computed) => {
        const label = template?.computedFields.find((field) => field.id === computed.id)?.label;
        return {
            key: `computed:${computed.id}`,
            label: label
                ? { text: label }
                : { extId: computed.referenceTypeExtId, kind: "type" as const },
            lines: toTextLines(computed.value),
            filters: [],
        };
    });

    return [...timingToRows(activity, template), ...slotRows, ...valueRows, ...computedRows];
};
